import { customFieldBuilderEn } from './custom-field-builder-i18n';
import { translationCenterEn } from './translation-center-i18n';
import { backupCenterEn, backupRestoreEn } from './backup-center-i18n';

/** Translations for the administration area; wired up in lib/i18n/en.ts. */
export const adminEn = {
  nav: {
    label: 'Administration',
    system: 'System & telemetry',
    errorLogs: 'Error center',
    customFields: 'Custom fields',
    auditVault: 'GoBD audit vault',
    dataHub: 'Import & export',
    mailBackups: 'Mail delivery',
    backups: 'Backup center',
    whatsapp: 'WhatsApp',
    telephonyAi: 'Telephony & AI',
  },
  errorLogs: {
    title: 'Error center',
    subtitle: 'Centrally logged system errors with their full context.',
    loadError: 'The error logs could not be loaded.',
    emptyTitle: 'No errors logged',
    emptyHint: 'No entries match the selected filters.',
    kpi: {
      unresolved: 'Open errors',
      last24h: 'Last 24 hours',
    },
    filters: {
      search: 'Search',
      searchPlaceholder: 'Tracking ID, message or endpoint',
      httpStatus: 'HTTP status',
      from: 'From',
      to: 'To',
      unresolvedOnly: 'Unresolved only',
      reset: 'Reset filters',
    },
    columns: {
      timestamp: 'Timestamp',
      status: 'Status',
      trackingId: 'Tracking ID',
      user: 'User',
      endpoint: 'Endpoint',
      message: 'Message',
      state: 'Handling',
    },
    state: {
      open: 'Open',
      resolved: 'Resolved',
    },
    detail: {
      title: 'Error details',
      ip: 'IP address',
      client: 'Client',
      message: 'Message',
      context: 'Business context',
      payload: 'Request payload',
      payloadHint: 'Passwords, tokens and bank details are removed before storing.',
      stackTrace: 'Stack trace',
      notes: 'Resolution note',
      resolved: 'The error was marked as resolved.',
      reopened: 'The error was reopened.',
      resolvedBy: 'Resolved by {{name}} on {{date}}.',
    },
    actions: {
      resolve: 'Mark as resolved',
      reopen: 'Reopen',
      openDocument: 'Open affected document',
      export: 'Export support report',
    },
  },
  status: {
    OK: 'OK',
    WARNING: 'Warning',
    CRITICAL: 'Critical',
  },
  system: {
    title: 'System & telemetry',
    subtitle: 'Runtime, database and server at a glance.',
    error: 'The telemetry could not be loaded.',
    refresh: 'Refresh',
    application: 'Application',
    database: 'Database',
    server: 'Server',
    version: 'Version',
    commit: 'Commit',
    node: 'Node.js',
    environment: 'Environment',
    uptime: 'Uptime',
    heap: 'Heap',
    dbSize: 'Database size',
    connections: 'Connections',
    streamClients: 'Live clients',
    scheduler: 'Background jobs',
    schedulerStopped: 'Not running',
    auditEntries: 'Audit entries',
    cores: 'CPU cores',
    loadAverage: 'Load average',
    memory: 'Memory',
    backupDisk: 'Backup storage',
    unavailable: 'Unavailable',
    collectedAt: 'Collected at',
    cpu: 'CPU load',
    eventLoop: 'Event loop {{value}} ms',
    cacheHit: 'Cache hit ratio {{value}} %',
    gauges: 'Utilisation',
    gaugesHint: 'Values refresh every 15 seconds.',
    features: 'Features and modules',
    featuresHint: 'Effective availability, configuration issues, and paused jobs.',
    featuresActive: 'Active',
    featuresDisabled: 'Disabled',
    featuresMisconfigured: 'Misconfigured',
    featuresBlocked: 'Dependencies blocked',
    featuresPausedJobs: 'Paused jobs',
    featuresLastChanges: 'Latest feature changes',
    featuresNoChanges: 'No changes recorded.',
    featureActive: 'active',
    featureInactive: 'inactive',
    quickActions: 'Quick actions',
    quickActionsHint: 'Maintenance tasks and caches.',
    clearCache: 'Clear cache',
    cacheCleared: 'Removed {{count}} cached files.',
    maintenanceMode: 'Maintenance mode',
    maintenanceHint: 'Blocks the application for everyone except the allowed addresses.',
    maintenanceMessage: 'Notice',
    allowedIps: 'Allowed IP addresses',
    allowedIpsHint: 'Comma separated, CIDR ranges such as 10.0.0.0/8 are allowed.',
    maintenanceSaved: 'Settings saved.',
  },
  customFields: {
    ...customFieldBuilderEn,
    translationCenter: translationCenterEn,
    title: 'Custom fields',
    subtitle: 'Own fields per data area, without code changes.',
    create: 'New custom field',
    created: 'Custom field created.',
    updated: 'Custom field saved.',
    archived: 'Custom field archived.',
    empty: 'No custom fields yet',
    emptyHint: 'Create a field to capture additional information.',
    dialogTitle: 'Create custom field',
    editTitle: 'Edit custom field',
    edit: 'Edit',
    dialogDescription: 'The field appears in the {{entity}} forms.',
    label: 'Display name',
    key: 'Key',
    keyHint: 'Technical name; fixed after creation.',
    description: 'Description',
    group: 'Category',
    groupPlaceholder: 'Select category',
    type: 'Data type',
    pattern: 'Validation regex',
    patternHint: 'Optional format check, e.g. ^[A-Z]{2}\\d{4}$.',
    options: 'Options',
    optionLabel: 'Option label',
    addOption: 'Add option',
    removeOption: 'Remove option',
    noOptions: 'No options yet',
    required: 'Required',
    inPdf: 'Print on PDF',
    searchable: 'Searchable',
    marketingSegmentationAllowed: 'Approve for newsletter segmentation',
    marketingSegmentationHint: 'Enable only after review and only for non-sensitive fields.',
    inList: 'Show in tables',
    inherit: 'Carry over to follow-up documents',
    inheritHint: 'The value follows quote → order → delivery note → invoice automatically.',
    inherited: 'Inherited',
    inItemTable: 'Show in item table',
    inItemTableHint: 'Appears below the item row and on the PDF.',
    copyFromProduct: 'Copy product value into the item',
    copyFromProductHint: 'Adding the product copies its master data value into the document item.',
    documentTypesTitle: 'Valid for document types',
    documentTypesHint: 'Without a selection the field applies to every document type.',
    preview: 'Live preview',
    previewHint: 'This is how the field appears in the form.',
    previewLabel: 'New field',
    moveUp: 'Move up',
    moveDown: 'Move down',
    archive: 'Archive',
    archiveTitle: 'Archive custom field',
    archiveDescription: 'The field “{{label}}” is hidden; captured values remain.',
    entities: {
      CUSTOMER: 'Business partners',
      CONTACT: 'Contacts',
      ADDRESS: 'Addresses and delivery addresses',
      PRODUCT: 'Products',
      PRODUCT_PACKAGING: 'Product packaging',
      DOCUMENT: 'Document header',
      DOCUMENT_ITEM: 'Document item row',
      BATCH: 'Batches',
    },
    documentTypes: {
      QUOTE: 'Quote',
      ORDER: 'Order',
      DELIVERY_NOTE: 'Delivery note',
      INVOICE: 'Invoice',
      PURCHASE_ORDER: 'Purchase order',
    },
    formula: {
      title: 'Formula',
      expression: 'Expression',
      expressionHint:
        'Allowed are + - * / parentheses, || for fallbacks and abs, min, max, round, floor and ceil.',
      invalid: 'The formula is invalid.',
      unit: 'Unit',
      unitHint: 'Shown after the result, e.g. €/pc.',
      decimals: 'Decimal places',
      testTitle: 'Live test',
      result: 'Result',
      variables: {
        title: 'Available variables – click to insert',
        quantity: 'Quantity',
        unitPrice: 'Unit price',
        discountPercent: 'Discount %',
        taxRate: 'Tax rate',
        lineNet: 'Line net',
        lineGross: 'Line gross',
        lineTotal: 'Line total',
      },
    },
    types: {
      WEEKLY_SCHEDULE: 'Weekdays & times',
      TEXT: 'Text',
      TEXTAREA: 'Multiline text',
      NUMBER: 'Integer',
      DECIMAL: 'Decimal',
      DATE: 'Date',
      CHECKBOX: 'Yes/No',
      SELECT: 'Single choice',
      MULTI_SELECT: 'Multiple choice',
      FORMULA: 'Calculated field',
    },
  },
  auditVault: {
    title: 'GoBD audit vault',
    subtitle: 'Complete, cryptographically chained change history.',
    error: 'The log could not be loaded.',
    empty: 'No entries for the selected filters.',
    integrityTitle: 'Integrity & tax data export',
    integrityHint: 'The check recomputes every hash and reports mismatches and missing numbers.',
    verify: 'Verify hash chain',
    exportCsv: 'GoBD export (CSV)',
    exportXml: 'GDPdU export (XML)',
    exportReady: 'Export created.',
    intact: 'All {{count}} entries are unaltered.',
    broken: '{{count}} integrity breaches found.',
    verifiedAt: 'Verified at {{timestamp}} in {{duration}} ms.',
    headHash: 'Head checksum',
    filterEntity: 'Filter entity',
    filterAction: 'Filter action',
    allEntities: 'All entities',
    allActions: 'All actions',
    from: 'From',
    to: 'To',
    timestamp: 'Timestamp',
    action: 'Action',
    entity: 'Entity',
    user: 'User',
    hash: 'Checksum',
    previousHash: 'Predecessor',
    origin: 'Origin',
    diffTitle: 'Log entry #{{sequence}}',
    field: 'Field',
    before: 'Before',
    after: 'After',
    noPayload: 'This action carries no payload.',
    changedFields: '{{count}} of {{total}} fields changed.',
    actions: {
      INSERT: 'Created',
      UPDATE: 'Updated',
      DELETE: 'Deleted',
      LOCK: 'Locked',
      EXPORT: 'Exported',
      REVERT: 'Reverted',
    },
    breaches: {
      HASH_MISMATCH: 'Checksum does not match the payload.',
      CHAIN_BROKEN: 'Link to the predecessor is broken.',
      SEQUENCE_GAP: 'Gap in the sequence numbers.',
    },
    entities: {
      Customer: 'Customer',
      Product: 'Product',
      ProductBatch: 'Batch',
      Document: 'Document',
      DocumentItem: 'Document item',
      StockMovement: 'Stock movement',
      DatevExportLog: 'DATEV export',
      IntrastatExportLog: 'Intrastat export',
      CompanyProfile: 'Company profile',
      CustomFieldDefinition: 'Custom field',
      VatIdVerification: 'VAT ID validation proof',
      DunningSettings: 'Dunning settings',
      DunningStage: 'Dunning level',
      CustomerDunningPolicy: 'Customer dunning terms',
      DunningRun: 'Dunning run',
      DunningNotice: 'Payment reminder',
      DunningNoticeSnapshot: 'Approved reminder snapshot',
      DunningNoticeItem: 'Reminded open item',
      DunningStatusEvent: 'Dunning status evidence',
      DunningDispatch: 'Reminder dispatch evidence',
      CustomerPayment: 'Customer payment',
      CustomerPaymentAllocation: 'Payment allocation',
      VatValidationSettings: 'VAT ID validation settings',
      ProductionLotGenealogyEdge: 'Lot genealogy edge',
      RecallSimulation: 'Recall simulation',
      RecallSimulationAnchor: 'Recall anchor lot',
      RecallSimulationAffectedBatch: 'Affected recall lot',
      RecallSimulationOperation: 'Recall operation',
    },
  },
  dataHub: {
    title: 'Import & export',
    subtitle: 'Take over master data from CSV or Excel files.',
    step1: 'Choose file',
    step1Hint: 'CSV or Excel via drag and drop or the file picker.',
    step2: 'Map columns',
    step2Hint: 'Columns with matching names are pre-assigned.',
    step3: 'Validate & import',
    step3Hint: 'The dry run writes nothing to the database.',
    target: 'Target area',
    dropHere: 'Drop the file here',
    formats: 'Supports .csv and .xlsx up to 10 MB.',
    chooseFile: 'Choose file',
    uploaded: '{{fileName}} · {{rows}} rows, {{columns}} columns detected.',
    sourceColumn: 'Source column',
    sample: 'Sample values',
    targetField: 'ERP field',
    ignore: 'Do not import',
    runDryRun: 'Start dry run',
    validRows: '{{count}} valid rows',
    invalidRows: '{{count}} invalid rows',
    row: 'Row',
    field: 'Field',
    reason: 'Reason',
    startImport: 'Import {{count}} rows',
    imported: 'Created {{success}} records, skipped {{failed}}.',
    history: 'Import history',
    historyHint: 'Completed runs can be reverted entirely.',
    noJobs: 'No import runs yet.',
    file: 'File',
    progress: 'Progress',
    status: 'Status',
    executedAt: 'Completed',
    rowSummary: '{{success}} imported · {{failed}} errors · {{total}} total',
    rollback: 'Revert',
    rolledBack: 'Import reverted.',
    entities: {
      CUSTOMER: 'Customers',
      PRODUCT: 'Products',
      STOCK_ADJUSTMENT: 'Stock adjustments',
    },
    statuses: {
      PENDING: 'Pending',
      PROCESSING: 'Running',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      ROLLED_BACK: 'Reverted',
    },
  },
  backupCenter: backupCenterEn,
  backupRestore: backupRestoreEn,
  mailBackups: {
    title: 'Mail delivery',
    subtitle: 'Check outgoing mail and SMTP configuration.',
    smtpTitle: 'SMTP diagnostics',
    smtpHint: 'Checks connection, encryption and authentication on the mail server.',
    testConnection: 'Test connection',
    sendTest: 'Send test message',
    recipient: 'Recipient',
    testSent: 'Test message sent.',
    testFailed: 'The test message could not be sent.',
    reachable: 'Reachable',
    unreachable: 'Unreachable',
    queueTitle: 'Failed document mails',
    queueHint: 'Resending is available directly on the document.',
    queueEmpty: 'No failed deliveries.',
    document: 'Document',
    errorMessage: 'Error',
    attemptedAt: 'Attempted at',
    backupsTitle: 'Backups',
    backupsHint: 'Encrypted archives (AES-256-GCM) in the backup directory.',
    createNow: 'Create backup now',
    backupCreated: 'Backup created.',
    noBackups: 'No backups available yet.',
    archive: 'Archive',
    size: 'Size',
    createdAt: 'Created at',
    checksum: 'Checksum',
    download: 'Download',
    delete: 'Delete',
    deleted: 'Backup deleted.',
    deleteTitle: 'Delete backup?',
    deleteDescription:
      'The archive "{{fileName}}" will be permanently removed from the server. Copies already downloaded remain unaffected.',
    writable: 'Directory writable',
    notWritable: 'Directory not writable',
    dumpAvailable: 'pg_dump available',
    dumpMissing: 'pg_dump missing — backups run via the host cron',
    cron: 'Schedule (cron)',
    retention: 'Retention (days)',
    storageTarget: 'Storage target',
    encrypted: 'Encrypt',
    scheduleActive: 'Schedule active',
    lastRun: 'Last run: {{timestamp}} · {{status}}',
    targets: {
      LOCAL: 'Local volume',
      S3: 'S3 compatible',
      OFFSITE_SSH: 'Offsite SSH storage',
    },
  },
} as const;
